import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type {
  AudioOutputOptions,
  JobScript,
  LanguageCodes,
  MediaSegment,
  PauseSegment,
  ScriptSegment,
  ScriptVariable,
  VoiceSegment,
  VoiceSpan,
} from "Infrastructure/Api/Api";
import {
  AudioBitrate,
  AudioChannels,
  AudioFormat,
  AudioSampleRate,
  JobScriptVersion,
} from "Infrastructure/Api/Api";
import {
  insertReactionAtOffset,
  reconciledSpans,
  sliceSpans,
} from "Projects/Utils/spanUtils";

// ---------------------------------------------------------------------------
// FE-side discriminated union types
// ---------------------------------------------------------------------------

export type VoiceSegmentFE = VoiceSegment & { type: "Voice" };
export type PauseSegmentFE = PauseSegment & { type: "Pause" };
export type MediaSegmentFE = MediaSegment & { type: "Media" };
export type ScriptSegmentFE = VoiceSegmentFE | PauseSegmentFE | MediaSegmentFE;

export type JobScriptFE = {
  variables: ScriptVariable[];
  segments: ScriptSegmentFE[];
  globalSpeedFactor: number;
  globalVolumePercent: number;
  defaultActorVoiceID: string | null;
  defaultLanguage: LanguageCodes | null;
};

export function isVoiceSegment(s: ScriptSegmentFE): s is VoiceSegmentFE {
  return s.type === "Voice";
}

export function isPauseSegment(s: ScriptSegmentFE): s is PauseSegmentFE {
  return s.type === "Pause";
}

export function isMediaSegment(s: ScriptSegmentFE): s is MediaSegmentFE {
  return s.type === "Media";
}

export function createVoiceSegment(
  overrides?: Partial<Omit<VoiceSegmentFE, "type">>,
): VoiceSegmentFE {
  return {
    type: "Voice",
    actorVoiceID: overrides?.actorVoiceID ?? "",
    languageOverride: overrides?.languageOverride ?? null,
    speedFactor: overrides?.speedFactor ?? 1.0,
    volumePercent: overrides?.volumePercent ?? 100,
    spans: overrides?.spans ?? [{ text: "", style: null }],
  };
}

export function createPauseSegment(durationMs = 3000): PauseSegmentFE {
  return { type: "Pause", durationMs };
}

export function createMediaSegment(): MediaSegmentFE {
  return {
    type: "Media",
    partnerMediaFileID: "",
    volumePercent: 100,
    startAtMs: 0,
  };
}

export function toJobScriptFE(script: JobScript): JobScriptFE {
  return {
    variables: script.variables,
    globalSpeedFactor: script.globalSpeedFactor ?? 1.0,
    globalVolumePercent: script.globalVolumePercent ?? 100,
    defaultActorVoiceID: (script as any).defaultActorVoiceID ?? null,
    defaultLanguage: (script as any).defaultLanguage ?? null,
    segments: script.segments.map((s): ScriptSegmentFE => {
      const raw = s as unknown as { type?: string; [key: string]: any };
      const segType = raw.type;
      if (segType === "Voice" || segType === "Pause" || segType === "Media") {
        return s as ScriptSegmentFE;
      }
      if ("spans" in raw)
        return { ...raw, type: "Voice" as const } as VoiceSegmentFE;
      if ("durationMs" in raw)
        return { ...raw, type: "Pause" as const } as PauseSegmentFE;
      return { ...raw, type: "Media" as const } as MediaSegmentFE;
    }),
  };
}

export function fromJobScriptFE(
  script: JobScriptFE,
  audioOutputOptions: AudioOutputOptions,
): JobScript {
  return {
    version: JobScriptVersion.V1,
    audioOutputOptions,
    variables: script.variables,
    segments: script.segments as unknown as ScriptSegment[],
    globalSpeedFactor: script.globalSpeedFactor,
    globalVolumePercent: script.globalVolumePercent,
    defaultActorVoiceID: script.defaultActorVoiceID,
    defaultLanguage: script.defaultLanguage,
  } as JobScript;
}

export interface UiDefaults {
  defaultActorVoiceID: string | null;
  defaultLanguage: LanguageCodes | null;
  defaultSpeedFactor: number;
  defaultVolumePercent: number;
}

const DEFAULT_UI_DEFAULTS: UiDefaults = {
  defaultActorVoiceID: null,
  defaultLanguage: null,
  defaultSpeedFactor: 1.0,
  defaultVolumePercent: 100,
};

const DEFAULT_AUDIO_OUTPUT: AudioOutputOptions = {
  format: AudioFormat.Mp3,
  sampleRate: AudioSampleRate.Hz22050,
  channels: AudioChannels.Stereo,
  bitrate: AudioBitrate.Kbps128,
};

export interface ActiveEditHandlers {
  segmentIndex: number;
  getTextAndSelection: () => { text: string; selStart: number; selEnd: number };
  cancelEdit: () => void;
  /**
   * Atomically flushes the current contenteditable content to segment spans
   * AND inserts an empty reaction span at `cursorOffset` (clean-text
   * position). Returns the index of the inserted reaction span in the
   * resulting `segment.spans` array. Doing both in one setState avoids the
   * async-update trap where reading the post-commit state from within a
   * second setState updater would not have completed yet.
   */
  commitAndInsertReactionAtCursor: (cursorOffset: number) => number;
}

export interface VoiceSpanReactionPatch {
  reaction: VoiceSpan["reaction"];
  reactionVolumeEnergy: VoiceSpan["reactionVolumeEnergy"];
  reactionPacingRhythm: VoiceSpan["reactionPacingRhythm"];
  customReaction: VoiceSpan["customReaction"];
}

export interface ScriptEditorContextValue {
  script: JobScriptFE;
  setScript: (script: JobScriptFE) => void;
  updateSegment: (index: number, segment: ScriptSegmentFE) => void;
  addSegment: (segment: ScriptSegmentFE, afterIndex?: number) => void;
  removeSegment: (index: number) => void;
  selectedSegmentIndex: number | null;
  setSelectedSegmentIndex: (i: number | null) => void;
  /**
   * Selects a segment AND emits a focus event (nonce bump) even if the same
   * segment was already selected. Use this for user-initiated focus actions
   * (segment clicks, adding a new segment). The sidebar listens to the nonce
   * so it can switch to the Parameters tab every time, even if the selection
   * value didn't change.
   */
  focusSegment: (i: number) => void;
  /** Monotonic counter bumped on every `focusSegment` call. */
  selectionNonce: number;
  /**
   * When set, the reaction edit modal is shown for the referenced span.
   * `isNew` flags reactions created via the "Add reaction" toolbar button —
   * cancelling such a modal removes the still-empty span so the user isn't
   * left with orphan chips.
   */
  reactionEditor: {
    segmentIndex: number;
    spanIndex: number;
    isNew: boolean;
  } | null;
  openReactionEditor: (
    segmentIndex: number,
    spanIndex: number,
    isNew?: boolean,
  ) => void;
  closeReactionEditor: () => void;
  uiDefaults: UiDefaults;
  setUiDefaults: (d: Partial<UiDefaults>) => void;
  audioOutputOptions: AudioOutputOptions;
  setAudioOutputOptions: (opts: AudioOutputOptions) => void;
  isSingleVoiceSpot: boolean;
  isEditable: boolean;
  applyVoiceToAllVoiceSegments: (actorVoiceID: string) => void;
  activeEditRef: React.MutableRefObject<ActiveEditHandlers | null>;
  /**
   * Reactive mirror of `activeEditRef.current?.segmentIndex`. Used by UI
   * controls (toolbar) that need to re-render when edit mode is entered or
   * exited. VoiceSegmentNode keeps this in sync.
   */
  activeEditSegmentIndex: number | null;
  setActiveEditSegmentIndex: (i: number | null) => void;
  splitVoiceSegment: (
    segmentIndex: number,
    text: string,
    selStart: number,
    selEnd: number,
    action: "voice" | "pause" | "media",
  ) => void;
  wrapSelectionWithVariable: (
    segmentIndex: number,
    text: string,
    selStart: number,
    selEnd: number,
    varName: string,
  ) => void;
  /**
   * Inserts a new empty reaction span at the cursor position and returns the
   * index where it was placed inside `segment.spans`. Callers typically open
   * the reaction editor immediately after.
   */
  insertReactionAtCursor: (
    segmentIndex: number,
    cursorOffset: number,
  ) => number;
  updateReactionSpan: (
    segmentIndex: number,
    spanIndex: number,
    patch: Partial<VoiceSpanReactionPatch>,
  ) => void;
  removeReactionSpan: (segmentIndex: number, spanIndex: number) => void;
}

const ScriptEditorContext = createContext<ScriptEditorContextValue | null>(
  null,
);

const EMPTY_SCRIPT: JobScriptFE = {
  variables: [],
  segments: [],
  globalSpeedFactor: 1.0,
  globalVolumePercent: 100,
  defaultActorVoiceID: null,
  defaultLanguage: null,
};

interface ScriptEditorProviderProps {
  initialScript?: JobScript | null;
  children: React.ReactNode;
  onScriptChange?: (script: JobScriptFE) => void;
  onAudioOutputOptionsChange?: (opts: AudioOutputOptions) => void;
  isSingleVoiceSpot?: boolean;
  isEditable?: boolean;
}

export function ScriptEditorProvider({
  initialScript,
  children,
  onScriptChange,
  onAudioOutputOptionsChange,
  isSingleVoiceSpot = false,
  isEditable = true,
}: ScriptEditorProviderProps) {
  const [script, setScriptState] = useState<JobScriptFE>(() =>
    initialScript ? toJobScriptFE(initialScript) : EMPTY_SCRIPT,
  );
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<
    number | null
  >(null);
  const [selectionNonce, setSelectionNonce] = useState(0);
  const focusSegment = useCallback((i: number) => {
    setSelectedSegmentIndex(i);
    setSelectionNonce((n) => n + 1);
  }, []);
  const [reactionEditor, setReactionEditor] = useState<{
    segmentIndex: number;
    spanIndex: number;
    isNew: boolean;
  } | null>(null);
  const [activeEditSegmentIndex, setActiveEditSegmentIndex] = useState<
    number | null
  >(null);
  const [uiDefaults, setUiDefaultsState] = useState<UiDefaults>(() => {
    if (initialScript) {
      const fe = toJobScriptFE(initialScript);
      return {
        ...DEFAULT_UI_DEFAULTS,
        defaultSpeedFactor: fe.globalSpeedFactor,
        defaultVolumePercent: fe.globalVolumePercent,
        defaultActorVoiceID: fe.defaultActorVoiceID,
        defaultLanguage: fe.defaultLanguage,
      };
    }
    return DEFAULT_UI_DEFAULTS;
  });
  const [audioOutputOptions, setAudioOutputOptions] =
    useState<AudioOutputOptions>(
      () => initialScript?.audioOutputOptions ?? DEFAULT_AUDIO_OUTPUT,
    );

  const uiDefaultsRef = useRef(uiDefaults);
  uiDefaultsRef.current = uiDefaults;

  const activeEditRef = useRef<ActiveEditHandlers | null>(null);

  const onScriptChangeRef = useRef(onScriptChange);
  onScriptChangeRef.current = onScriptChange;

  const onAudioOutputOptionsChangeRef = useRef(onAudioOutputOptionsChange);
  onAudioOutputOptionsChangeRef.current = onAudioOutputOptionsChange;
  const isInitialMountRef = useRef(true);
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    onScriptChangeRef.current?.(script);
  }, [script]);

  useEffect(() => {
    onAudioOutputOptionsChangeRef.current?.(audioOutputOptions);
  }, [audioOutputOptions]);

  const setScript = useCallback((s: JobScriptFE) => {
    setScriptState(s);
  }, []);

  const updateSegment = useCallback(
    (index: number, segment: ScriptSegmentFE) => {
      setScriptState((prev) => {
        const segments = [...prev.segments];
        segments[index] = segment;
        return { ...prev, segments };
      });
    },
    [],
  );

  const addSegment = useCallback(
    (segment: ScriptSegmentFE, afterIndex?: number) => {
      let insertedAt = -1;
      setScriptState((prev) => {
        const segments = [...prev.segments];
        insertedAt =
          afterIndex !== undefined ? afterIndex + 1 : segments.length;
        segments.splice(insertedAt, 0, segment);
        return { ...prev, segments };
      });
      // Focus the new segment so the Parameters tab opens in the sidebar.
      if (insertedAt >= 0) {
        focusSegment(insertedAt);
      }
    },
    [focusSegment],
  );

  const removeSegment = useCallback((index: number) => {
    setScriptState((prev) => {
      const segments = prev.segments.filter((_, i) => i !== index);
      return { ...prev, segments };
    });
    setSelectedSegmentIndex((sel) => {
      if (sel === null || sel === index) return null;
      if (sel > index) return sel - 1;
      return sel;
    });
    setReactionEditor(null);
  }, []);

  const setUiDefaults = useCallback((d: Partial<UiDefaults>) => {
    setUiDefaultsState((prev) => ({ ...prev, ...d }));
    if (
      d.defaultSpeedFactor !== undefined ||
      d.defaultVolumePercent !== undefined ||
      d.defaultActorVoiceID !== undefined ||
      d.defaultLanguage !== undefined
    ) {
      setScriptState((prev) => ({
        ...prev,
        ...(d.defaultSpeedFactor !== undefined && {
          globalSpeedFactor: d.defaultSpeedFactor,
        }),
        ...(d.defaultVolumePercent !== undefined && {
          globalVolumePercent: d.defaultVolumePercent,
        }),
        ...(d.defaultActorVoiceID !== undefined && {
          defaultActorVoiceID: d.defaultActorVoiceID,
        }),
        ...(d.defaultLanguage !== undefined && {
          defaultLanguage: d.defaultLanguage,
        }),
      }));
    }
  }, []);

  const applyVoiceToAllVoiceSegments = useCallback((actorVoiceID: string) => {
    setScriptState((prev) => ({
      ...prev,
      segments: prev.segments.map((s) =>
        isVoiceSegment(s) ? { ...s, actorVoiceID } : s,
      ),
    }));
    setUiDefaultsState((prev) => ({
      ...prev,
      defaultActorVoiceID: actorVoiceID,
    }));
  }, []);

  const splitVoiceSegment = useCallback(
    (
      segmentIndex: number,
      text: string,
      selStart: number,
      selEnd: number,
      action: "voice" | "pause" | "media",
    ) => {
      const before = text.slice(0, selStart);
      const insertedAt = segmentIndex + (before ? 1 : 0);

      setScriptState((prev) => {
        const originalSeg = prev.segments[segmentIndex];
        if (!isVoiceSegment(originalSeg)) return prev;

        const selected = text.slice(selStart, selEnd);
        const after = text.slice(selEnd);

        const makeVoice = (t: string, charStart: number): VoiceSegmentFE => ({
          ...originalSeg,
          spans: sliceSpans(originalSeg.spans, charStart, charStart + t.length),
        });

        const newSegs: ScriptSegmentFE[] = [];
        if (before) newSegs.push(makeVoice(before, 0));

        if (action === "voice") {
          newSegs.push(
            createVoiceSegment({
              actorVoiceID: "",
              languageOverride: uiDefaultsRef.current.defaultLanguage,
              speedFactor: uiDefaultsRef.current.defaultSpeedFactor,
              volumePercent: uiDefaultsRef.current.defaultVolumePercent,
              spans: [{ text: selected, style: null }],
            }),
          );
        } else if (action === "pause") {
          newSegs.push(createPauseSegment());
        } else {
          newSegs.push(createMediaSegment());
        }

        if (after) newSegs.push(makeVoice(after, selEnd));

        if (newSegs.length === 0) return prev;

        const segs = [...prev.segments];
        segs.splice(segmentIndex, 1, ...newSegs);
        return { ...prev, segments: segs };
      });

      // Focus the newly-inserted segment so the Parameters tab opens.
      focusSegment(insertedAt);
    },
    [focusSegment],
  );

  const wrapSelectionWithVariable = useCallback(
    (
      segmentIndex: number,
      text: string,
      selStart: number,
      selEnd: number,
      varName: string,
    ) => {
      const newText = `${text.slice(0, selStart)}{{${varName}}}${text.slice(selEnd)}`;
      setScriptState((prev) => {
        const originalSeg = prev.segments[segmentIndex];
        if (!isVoiceSegment(originalSeg)) return prev;
        // Reconcile the new flat text against existing spans so emotion
        // spans and reaction spans are preserved (reactions at positions
        // outside the selection stay put; reactions inside the selection
        // collapse to the insertion point).
        const newSpans = reconciledSpans(originalSeg.spans, newText);
        const updatedSeg: VoiceSegmentFE = {
          ...originalSeg,
          spans: newSpans,
        };
        const segs = [...prev.segments];
        segs[segmentIndex] = updatedSeg;
        const variables = prev.variables.some((v) => v.name === varName)
          ? prev.variables
          : [...prev.variables, { name: varName, defaultValue: "" }];
        return { ...prev, segments: segs, variables };
      });
    },
    [],
  );

  const insertReactionAtCursor = useCallback(
    (segmentIndex: number, cursorOffset: number): number => {
      let insertedIndexOut = -1;
      setScriptState((prev) => {
        const originalSeg = prev.segments[segmentIndex];
        if (!isVoiceSegment(originalSeg)) return prev;
        const { spans: newSpans, insertedIndex } = insertReactionAtOffset(
          originalSeg.spans,
          cursorOffset,
        );
        insertedIndexOut = insertedIndex;
        const segs = [...prev.segments];
        segs[segmentIndex] = { ...originalSeg, spans: newSpans };
        return { ...prev, segments: segs };
      });
      focusSegment(segmentIndex);
      return insertedIndexOut;
    },
    [focusSegment],
  );

  const updateReactionSpan = useCallback(
    (
      segmentIndex: number,
      spanIndex: number,
      patch: Partial<VoiceSpanReactionPatch>,
    ) => {
      setScriptState((prev) => {
        const seg = prev.segments[segmentIndex];
        if (!isVoiceSegment(seg)) return prev;
        const span = seg.spans[spanIndex];
        if (!span) return prev;
        const updatedSpan: VoiceSpan = { ...span, ...patch };
        const newSpans = [...seg.spans];
        newSpans[spanIndex] = updatedSpan;
        const segs = [...prev.segments];
        segs[segmentIndex] = { ...seg, spans: newSpans };
        return { ...prev, segments: segs };
      });
    },
    [],
  );

  const removeReactionSpan = useCallback(
    (segmentIndex: number, spanIndex: number) => {
      setScriptState((prev) => {
        const seg = prev.segments[segmentIndex];
        if (!isVoiceSegment(seg)) return prev;
        const newSpans = seg.spans.filter((_, i) => i !== spanIndex);
        const segs = [...prev.segments];
        segs[segmentIndex] = { ...seg, spans: newSpans };
        return { ...prev, segments: segs };
      });
      setReactionEditor(null);
    },
    [],
  );

  const openReactionEditor = useCallback(
    (segmentIndex: number, spanIndex: number, isNew = false) => {
      setReactionEditor({ segmentIndex, spanIndex, isNew });
    },
    [],
  );

  const closeReactionEditor = useCallback(() => {
    setReactionEditor(null);
  }, []);

  const value = useMemo<ScriptEditorContextValue>(
    () => ({
      script,
      setScript,
      updateSegment,
      addSegment,
      removeSegment,
      selectedSegmentIndex,
      setSelectedSegmentIndex,
      focusSegment,
      selectionNonce,
      reactionEditor,
      openReactionEditor,
      closeReactionEditor,
      uiDefaults,
      setUiDefaults,
      audioOutputOptions,
      setAudioOutputOptions,
      isSingleVoiceSpot,
      isEditable,
      applyVoiceToAllVoiceSegments,
      activeEditRef,
      activeEditSegmentIndex,
      setActiveEditSegmentIndex,
      splitVoiceSegment,
      wrapSelectionWithVariable,
      insertReactionAtCursor,
      updateReactionSpan,
      removeReactionSpan,
    }),
    [
      script,
      setScript,
      updateSegment,
      addSegment,
      removeSegment,
      selectedSegmentIndex,
      focusSegment,
      selectionNonce,
      reactionEditor,
      openReactionEditor,
      closeReactionEditor,
      uiDefaults,
      setUiDefaults,
      audioOutputOptions,
      isSingleVoiceSpot,
      isEditable,
      applyVoiceToAllVoiceSegments,
      splitVoiceSegment,
      wrapSelectionWithVariable,
      insertReactionAtCursor,
      updateReactionSpan,
      removeReactionSpan,
      activeEditSegmentIndex,
    ],
  );

  return (
    <ScriptEditorContext.Provider value={value}>
      {children}
    </ScriptEditorContext.Provider>
  );
}

export function useScriptEditor(): ScriptEditorContextValue {
  const ctx = useContext(ScriptEditorContext);
  if (!ctx) {
    throw new Error(
      "useScriptEditor must be used within a ScriptEditorProvider",
    );
  }
  return ctx;
}
